/**
 * RescheduleModal — TASK-677.
 *
 * A minimal date/time picker that moves a meeting to a new instant. It reuses the TASK-674
 * timezone contract: `new Date(`${date}T${time}`).toISOString()` parses the RM's local
 * wall-clock and sends a real UTC instant — never a naive time stamped with `Z`.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { rescheduleMeeting } from '../../services/api/meetingApi';
import { useModalDismiss } from '../../hooks/useModalDismiss';

interface RescheduleModalProps {
  sessionId: string;
  clientName: string;
  /** Current scheduled_at (ISO, UTC) to prefill the inputs in local time. */
  currentIso?: string | null;
  onClose: () => void;
  onRescheduled: () => void;
}

function toLocalParts(iso?: string | null): { date: string; time: string } {
  const d = iso ? new Date(iso) : new Date(Date.now() + 24 * 3600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export function RescheduleModal({
  sessionId,
  clientName,
  currentIso,
  onClose,
  onRescheduled,
}: RescheduleModalProps) {
  const initial = toLocalParts(currentIso);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [busy, setBusy] = useState(false);
  // Deep review #6: Escape closes, focus starts inside the dialog and returns to the opener.
  const dialogRef = useModalDismiss<HTMLDivElement>(onClose);

  async function submit() {
    if (!date || !time) return;
    setBusy(true);
    try {
      const iso = new Date(`${date}T${time}`).toISOString();
      await rescheduleMeeting(sessionId, iso);
      toast.success('Meeting rescheduled');
      onRescheduled();
      onClose();
    } catch {
      toast.error('Could not reschedule the meeting. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Reschedule meeting"
        className="w-full max-w-sm rounded-2xl border border-border bg-background p-6 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Reschedule meeting</h2>
            <p className="text-sm text-muted-foreground">{clientName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel reschedule"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Date
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm font-medium text-foreground">
            Time
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !date || !time}
            className="rounded-lg bg-kielis-navy px-3 py-2 text-sm font-medium text-white hover:bg-kielis-navy/90 disabled:opacity-50"
          >
            {busy ? 'Rescheduling…' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RescheduleModal;
